import React, { useState, useRef, useEffect } from 'react';
import './popup.scss';
import TaskFields from './TaskFields';
import InquiryFields from './InquiryFields';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import { postWithFile, postWithToken } from '../../../../services/apiRequest';
import { showToast } from '../../../atom/toaster/toaster';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserRoleDetailPopup = ({ isOpen, selectedRow, rowType = 'task', onClose, onOverlayClick, onProductLineClick, categories = {}, onUpdate, products = [], partners = [] }) => {
  const { access_token } = useAuthContext();
  const [filesToUpload, setFilesToUpload] = useState({}); // { fieldName: [File objects] }
  const [filesToDelete, setFilesToDelete] = useState({}); // { fieldName: [fileIds] }
  const [loading, setLoading] = useState(false);
  const fileInputRefs = useRef({});
  const formRef = useRef(null);

  // Cleanup file inputs on unmount
  useEffect(() => {
    return () => {
      // Cleanup file inputs
      Object.values(fileInputRefs.current).forEach(input => {
        if (input && input.parentNode) {
          input.parentNode.removeChild(input);
        }
      });
    };
  }, []);

  // Auto-add name attributes to form fields based on their values
  // This ensures all fields can be identified and collected
  useEffect(() => {
    if (formRef.current && selectedRow) {
      const formElements = formRef.current.elements;
      const processedElements = new Set();
      
      // Helper to normalize values for comparison
      const normalizeForComparison = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') return val.trim().toLowerCase();
        return String(val).toLowerCase();
      };
      
      for (let element of formElements) {
        // Skip if already has name attribute
        if (element.name) {
          processedElements.add(element);
          continue;
        }
        
        // Skip buttons, file inputs
        if (element.type === 'button' || element.type === 'submit' || element.type === 'file') {
          continue;
        }
        
        // Skip if already processed
        if (processedElements.has(element)) {
          continue;
        }
        
        processedElements.add(element);
        
        // Get the defaultValue or value
        const elementValue = element.defaultValue || element.value;
        
        // Try to find matching property in selectedRow
        if (elementValue !== null && elementValue !== undefined && elementValue !== '') {
          let matchingKey = null;
          
          // For select elements, match by selected value
          if (element.tagName === 'SELECT') {
            const selectedValue = element.options[element.selectedIndex]?.value;
            if (selectedValue) {
              // Find property in selectedRow that matches this value
              matchingKey = Object.keys(selectedRow).find(key => {
                const rowValue = selectedRow[key];
                if (typeof rowValue === 'object' && rowValue !== null && !Array.isArray(rowValue)) {
                  return String(rowValue.id) === String(selectedValue) || String(rowValue.value) === String(selectedValue);
                }
                return String(rowValue) === String(selectedValue);
              });
            }
          } else {
            // For other elements, try to match by value
            matchingKey = Object.keys(selectedRow).find(key => {
              const rowValue = selectedRow[key];
              if (rowValue === null || rowValue === undefined) return false;
              
              // Handle formatted dates
              if (element.type === 'date' && rowValue) {
                try {
                  const dateValue = new Date(rowValue);
                  const formattedDate = dateValue.toISOString().split('T')[0];
                  return formattedDate === elementValue;
                } catch (e) {
                  return String(rowValue) === elementValue;
                }
              }
              
              // Handle HTML stripped values (for textareas)
              if (element.tagName === 'TEXTAREA' && typeof rowValue === 'string') {
                const stripped = rowValue.replace(/<[^>]*>/g, '').trim();
                return stripped === elementValue || String(rowValue) === elementValue;
              }
              
              // Try exact match first
              if (String(rowValue) === String(elementValue)) {
                return true;
              }
              
              // Try normalized comparison
              return normalizeForComparison(rowValue) === normalizeForComparison(elementValue);
            });
          }
          
          if (matchingKey) {
            element.name = matchingKey;
            element.setAttribute('data-field-matched', 'true');
          } else {
            // If no match found, try to get from label
            const label = element.closest('.form-group')?.querySelector('label');
            if (label) {
              const labelText = label.textContent.trim();
              // Try to find a property that might match the label (fallback)
              const possibleMatch = Object.keys(selectedRow).find(key => {
                // This is a fallback - not perfect but helps with edge cases
                return false; // Disable for now to avoid false matches
              });
              if (possibleMatch) {
                element.name = possibleMatch;
              }
            }
          }
        }
      }
      
      // Log which fields got name attributes
      const fieldsWithNames = Array.from(formElements)
        .filter(el => el.name && el.type !== 'button' && el.type !== 'submit' && el.type !== 'file')
        .map(el => ({ name: el.name, type: el.type || el.tagName }));
      
      if (fieldsWithNames.length > 0) {
        console.log('Fields with name attributes:', fieldsWithNames);
      }
    }
  }, [selectedRow, isOpen]);

  if (!isOpen || !selectedRow) {
    return null;
  }


  // Helper function to format ISO date string to YYYY-MM-DD format
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid date
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      return dateString; // Return original if parsing fails
    }
  };

  // Helper function to strip HTML tags and convert to plain text with proper line breaks
  const stripHtmlAndFormat = (htmlString) => {
    if (!htmlString) return '';
    
    // First, replace block-level HTML elements with line breaks
    let text = htmlString
      .replace(/<br\s*\/?>/gi, '\n')        // Replace <br> tags with newlines
      .replace(/<\/p>/gi, '\n')              // Replace </p> with newline
      .replace(/<p[^>]*>/gi, '')             // Remove opening <p> tags
      .replace(/<\/div>/gi, '\n')            // Replace </div> with newline
      .replace(/<div[^>]*>/gi, '')           // Remove opening <div> tags
      .replace(/<\/li>/gi, '\n')             // Replace </li> with newline
      .replace(/<li[^>]*>/gi, '• ')          // Replace <li> with bullet point
      .replace(/<\/ul>/gi, '\n')             // Replace </ul> with newline
      .replace(/<ul[^>]*>/gi, '')            // Remove opening <ul> tags
      .replace(/<\/ol>/gi, '\n')             // Replace </ol> with newline
      .replace(/<ol[^>]*>/gi, '')            // Remove opening <ol> tags
      .replace(/<[^>]+>/g, '')               // Remove all remaining HTML tags
      .replace(/&nbsp;/gi, ' ')              // Replace &nbsp; with space
      .replace(/&amp;/gi, '&')               // Replace &amp; with &
      .replace(/&lt;/gi, '<')                // Replace &lt; with <
      .replace(/&gt;/gi, '>')                // Replace &gt; with >
      .replace(/&quot;/gi, '"')              // Replace &quot; with "
      .replace(/&#39;/gi, "'")               // Replace &#39; with '
      .replace(/&#160;/gi, ' ')              // Replace &#160; with space
      .replace(/\n\s*\n\s*\n+/g, '\n\n')     // Replace 3+ consecutive newlines with double newline
      .replace(/[ \t]+/g, ' ')               // Replace multiple spaces/tabs with single space
      .replace(/^\s+|\s+$/gm, '')            // Trim each line
      .trim();                                // Trim the entire string
    
    return text;
  };

  // Extract countryside data from selectedRow
  // Handle both cases: when countryside is a string (from calendar) or an object (from table)
  let countrysideValue = '';
  if (selectedRow.countryside) {
    if (typeof selectedRow.countryside === 'string') {
      // Countryside is a string (from calendar view)
      countrysideValue = selectedRow.countryside;
    } else if (typeof selectedRow.countryside === 'object' && selectedRow.countryside.value !== undefined) {
      // Countryside is an object with value property
      countrysideValue = selectedRow.countryside.value;
    }
  }

  // Use available options if countryside.options exists, otherwise use default options
  const countrysideOptions = selectedRow.countryside && 
                             typeof selectedRow.countryside === 'object' && 
                             Array.isArray(selectedRow.countryside.options)
    ? selectedRow.countryside.options.map(option => ({
        value: option,
        label: option
      }))
    : [];

  // Extract the correct category object based on rowType
  // Categories structure: { tasks: {...}, inquiry: {...}, product_line: {...} }
  const currentCategories = rowType === 'enquiry' 
    ? (categories.inquiry || {})
    : (categories.tasks || {});

  // Helper function to transform category arrays from {id, value} or {id, name} to {value, label} format
  const transformCategoryOptions = (categoryArray, fieldLabel = null, includeNullOption = true) => {
    if (!Array.isArray(categoryArray) || categoryArray.length === 0) {
      // If null option should be included, return it; otherwise return empty array
      if (includeNullOption) {
        let nullOptionLabel = 'Vælg';
        if (fieldLabel) {
          nullOptionLabel = `Vælg ${fieldLabel}`;
        }
        return [{ value: '', label: nullOptionLabel }];
      }
      return [];
    }
    
    // Add all category options
    const categoryOptions = categoryArray.map(item => ({
      value: String(item.id),
      label: item.name || item.value || item.label || String(item.id)
    }));
    
    // Add null/empty option at the beginning if requested
    if (includeNullOption) {
      let nullOptionLabel = 'Vælg';
      if (fieldLabel) {
        nullOptionLabel = `Vælg ${fieldLabel}`;
      }
      return [{ value: '', label: nullOptionLabel }, ...categoryOptions];
    }
    
    return categoryOptions;
  };

  // Helper function to get category value from selectedRow
  // Gets value from selectedRow[fieldName] and matches it with category options to return the ID
  const getCategoryValue = (fieldName, categoryArray) => {
    // Get the value from selectedRow using the field name (e.g., selectedRow.housing_type, selectedRow.status)
    const fieldValue = selectedRow[fieldName];
    if (fieldValue === null || fieldValue === undefined || fieldValue === '') return '';
    
    // If it's an object with id, return the id as string
    if (typeof fieldValue === 'object' && fieldValue.id !== undefined) {
      return String(fieldValue.id);
    }
    
    // If it's a number, it might already be an ID - verify it exists in categoryArray
    if (typeof fieldValue === 'number') {
      const idExists = categoryArray && Array.isArray(categoryArray) && 
        categoryArray.some(cat => cat.id === fieldValue);
      if (idExists) {
        return String(fieldValue);
      }
    }
    
    // If it's a string (text value from database), match it with category options
    // The database stores text values like "Hus" or "Modtaget", but SelectField needs IDs like "1"
    // Support both 'value' and 'name' properties for backward compatibility
    if (typeof fieldValue === 'string' && categoryArray && Array.isArray(categoryArray)) {
      // First, check if it's a numeric string that matches an ID
      const numericValue = Number(fieldValue);
      if (!isNaN(numericValue) && isFinite(numericValue)) {
        const idMatch = categoryArray.find(cat => cat.id === numericValue);
        if (idMatch) {
          return String(idMatch.id);
        }
      }
      
      // Then try exact match (including ID as string)
      let matchedCategory = categoryArray.find(cat => 
        cat.value === fieldValue || 
        cat.name === fieldValue ||
        String(cat.id) === fieldValue
      );
      
      // If no exact match, try case-insensitive match
      if (!matchedCategory) {
        matchedCategory = categoryArray.find(cat => 
          (cat.value && cat.value.toLowerCase().trim() === fieldValue.toLowerCase().trim()) ||
          (cat.name && cat.name.toLowerCase().trim() === fieldValue.toLowerCase().trim())
        );
      }
      
      // For price field specifically, also try matching with trimmed whitespace and normalized formatting
      if (!matchedCategory && fieldName === 'price') {
        const normalizedFieldValue = fieldValue.trim().replace(/\s+/g, ' ');
        matchedCategory = categoryArray.find(cat => {
          if (cat.value) {
            const normalizedCatValue = cat.value.trim().replace(/\s+/g, ' ');
            return normalizedCatValue === normalizedFieldValue;
          }
          return false;
        });
      }
      
      if (matchedCategory) {
        return String(matchedCategory.id);
      }
    }
    
    // Fallback: return empty string instead of the fieldValue to prevent showing wrong values
    // This ensures SelectField shows empty if no match is found
    console.warn(`getCategoryValue: No match found for field "${fieldName}" with value:`, fieldValue, 'Available categories:', categoryArray);
    return '';
  };

  // Helper function to get product value from selectedRow
  // If add_product has item_id, return that item_id as string for the select field
  const getProductValue = () => {
    const addProduct = selectedRow.add_product;
    if (!addProduct) return '';
    
    // If it's an object with item_id, return the item_id as string
    if (typeof addProduct === 'object' && addProduct.item_id !== undefined) {
      return String(addProduct.item_id);
    }
    
    // If it's a number, it might already be an item_id
    if (typeof addProduct === 'number') {
      return String(addProduct);
    }
    
    // If it's a string, try to find matching product by item_id
    if (typeof addProduct === 'string' && products && Array.isArray(products)) {
      const matchedProduct = products.find(prod => 
        String(prod.item_id) === addProduct || 
        String(prod.id) === addProduct
      );
      if (matchedProduct) {
        return String(matchedProduct.item_id);
      }
    }
    
    return '';
  };

  // Transform products to SelectField options format
  const transformProductOptions = () => {
    if (!Array.isArray(products) || products.length === 0) {
      return [{ value: '', label: 'Vælg produkt' }];
    }
    
    return [
      { value: '', label: 'Vælg produkt' },
      ...products.map(product => ({
        value: String(product.item_id),
        label: product.name || product.product_name || String(product.item_id)
      }))
    ];
  };

  // Helper function to get partner value from selectedRow
  // Partner field stores item_id, return that item_id as string for the select field
  const getPartnerValue = () => {
    // First try to get from partner field
    const partner = selectedRow.partner;
    if (partner) {
      // If it's an object with item_id, return the item_id as string
      if (typeof partner === 'object' && partner.item_id !== undefined) {
        return String(partner.item_id);
      }
      
      // If it's a number, it might already be an item_id
      if (typeof partner === 'number') {
        return String(partner);
      }
      
      // If it's a string, try to find matching partner by item_id
      if (typeof partner === 'string' && partners && Array.isArray(partners)) {
        const matchedPartner = partners.find(part => 
          String(part.item_id) === partner || 
          String(part.id) === partner
        );
        if (matchedPartner) {
          return String(matchedPartner.item_id || matchedPartner.id);
        }
      }
    }
    
    // Fallback: check user_item_id directly if partner field is not available
    if (selectedRow.user_item_id) {
      return String(selectedRow.user_item_id);
    }
    
    return '';
  };

  // Transform partners to SelectField options format
  const transformPartnerOptions = () => {
    if (!Array.isArray(partners) || partners.length === 0) {
      return [{ value: '', label: 'Vælg partner' }];
    }
    
    return [
      { value: '', label: 'Vælg partner' },
      ...partners.map(partner => ({
        value: String(partner.item_id || partner.id),
        label: partner.name || String(partner.item_id || partner.id)
      }))
    ];
  };

  // Handler for removing an image
  const handleRemoveImage = (fieldName, fileId, index) => {
    if (fileId) {
      // File is already uploaded - mark for deletion
      setFilesToDelete(prev => {
        const current = prev[fieldName] || [];
        return {
          ...prev,
          [fieldName]: [...current, fileId]
        };
      });
    } else {
      // File is pending upload - remove from upload queue
      setFilesToUpload(prev => {
        const current = prev[fieldName] || [];
        const updated = current.filter((_, i) => i !== index);
        return {
          ...prev,
          [fieldName]: updated.length > 0 ? updated : undefined
        };
      });
    }
  };

  // Handler for adding a new image
  const handleAddImage = (fieldName) => {
    // Create a hidden file input if it doesn't exist
    if (!fileInputRefs.current[fieldName]) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpg,image/png,image/jpeg,image/heif,image/gif,image/webp';
      input.multiple = true;
      input.style.display = 'none';
      
      input.onchange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        if (selectedFiles.length > 0) {
          // Filter only image files
          const imageFiles = selectedFiles.filter(file => {
            return file.type.startsWith('image/');
          });

          if (imageFiles.length !== selectedFiles.length) {
            showToast(
              "Error",
              "Some files were not images and were filtered out.",
              {
                position: "top-center",
                autoClose: 3000,
              }
            );
          }

          if (imageFiles.length > 0) {
            setFilesToUpload(prev => {
              const current = prev[fieldName] || [];
              return {
                ...prev,
                [fieldName]: [...current, ...imageFiles]
              };
            });
          }
        }
        // Reset input value to allow selecting the same file again
        e.target.value = '';
      };
      
      fileInputRefs.current[fieldName] = input;
      document.body.appendChild(input);
    }
    
    // Trigger file picker
    fileInputRefs.current[fieldName].click();
  };

  // Helper function to convert category ID to {id, value} or {id, name} object
  const getCategoryObject = (fieldName, categoryId, categoryArray) => {
    // Handle null/empty option (empty string means "no selection")
    if (!categoryId || categoryId === '' || categoryId === null) return null;
    
    const id = typeof categoryId === 'string' ? parseInt(categoryId, 10) : categoryId;
    if (isNaN(id)) return null;
    
    if (categoryArray && Array.isArray(categoryArray)) {
      const category = categoryArray.find(cat => cat.id === id);
      if (category) {
        // Support both 'name' (new format) and 'value' (old format) for backward compatibility
        const categoryValue = category.name || category.value;
        return {
          id: category.id,
          name: category.name,
          value: categoryValue
        };
      }
    }
    
    // Fallback: return with just the ID if category not found
    return {
      id: id,
      name: null,
      value: null
    };
  };

  // Helper function to format date to ISO string
  const formatDateToISO = (dateString) => {
    if (!dateString || dateString === '' || dateString === null) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.toISOString();
    } catch (error) {
      return null;
    }
  };

  // Helper function to format date range to {start, end} object
  const formatDateRange = (startDate, endDate) => {
    const start = formatDateToISO(startDate);
    const end = formatDateToISO(endDate);
    
    if (!start && !end) return null;
    if (!start) return { end };
    if (!end) return { start };
    
    return { start, end };
  };

  // Helper function to format form data according to API requirements
  const formatFormDataForAPI = (formFields, rowType) => {
    const itemId = selectedRow.item_id || selectedRow.id;
    const formattedData = {
      item_id: itemId
    };

    // Helper to set value or null
    const setValue = (key, value) => {
      if (value === '' || value === undefined) {
        formattedData[key] = null;
      } else {
        formattedData[key] = value;
      }
    };

    if (rowType === 'task') {
      // Task fields mapping - include all fields, set empty ones to null
      setValue('customer_first_name', formFields.customer_first_name);
      setValue('customer_last_name', formFields.customer_last_name);
      setValue('customer_address', formFields.customer_address);
      setValue('customer_email', formFields.customer_email);
      setValue('customer_phone_number', formFields.customer_phone_number);
      
      // Category fields - convert to {id, value} objects, set to null if empty
      formattedData.housing_type = getCategoryObject('housing_type', formFields.housing_type, currentCategories.housing_type);
      formattedData.status = getCategoryObject('status', formFields.status, currentCategories.status);
      formattedData.customer_has_paid = getCategoryObject('customer_has_paid', formFields.customer_has_paid, currentCategories.customer_has_paid);
      formattedData.subcontractor_has_paid = getCategoryObject('subcontractor_has_paid', formFields.subcontractor_has_paid, currentCategories.subcontractor_has_paid);
      formattedData.type = getCategoryObject('type', formFields.type, currentCategories.type);
      formattedData.product_type = getCategoryObject('product_type', formFields.product_type, currentCategories.product_type);
      
      setValue('estimated_material_usage', formFields.estimated_material_usage);
      
      // Date fields
      // Udførelsesdato (dato-for-hvornar-opgaven-skal-pabegyndes) maps to start_date only
      const startDate = formFields.start_date || selectedRow.start_date;
      formattedData.start_date = startDate ? { start: formatDateToISO(startDate) } : null;
      
      setValue('next_sms_date_for_partners', formatDateToISO(formFields.next_sms_date_for_partners));
      // Deadline Date (deadline-date) maps to end_date in database
      // Support both end_date and deadline_date for backward compatibility
      const deadlineDateValue = formFields.end_date || formFields.deadline_date || selectedRow.end_date || selectedRow.deadline_date;
      setValue('end_date', formatDateToISO(deadlineDateValue));
      // Also set deadline_date for backward compatibility
      setValue('deadline_date', formatDateToISO(deadlineDateValue));
      setValue('sms_time_for_customer', formFields.sms_time_for_customer);
      setValue('final_deadline_date', formatDateToISO(formFields.final_deadline_date));
      setValue('paid_amount_to_painter', formFields.paid_amount_to_painter ? parseFloat(formFields.paid_amount_to_painter) : null);
      setValue('extra_services', formFields.extra_services);
      setValue('free_text_for_additional_services', formFields.free_text_for_additional_services);
      setValue('internal_notes', formFields.internal_notes);
      setValue('internal_notes_to_admin', formFields.internal_notes_to_admin);
      setValue('invoice_description', formFields.invoice_description);
      
      // Add product field - send item_id directly
      const addProductValue = formFields.add_product;
      if (addProductValue && addProductValue !== '' && addProductValue !== null) {
        // Parse as number (item_id) and send directly
        const itemId = typeof addProductValue === 'string' ? parseInt(addProductValue, 10) : addProductValue;
        formattedData.add_product = isNaN(itemId) ? null : itemId;
      } else {
        formattedData.add_product = null;
      }
      
      setValue('price', formFields.price ? parseFloat(formFields.price) : null);
      setValue('estimated_task_time', formFields.estimated_task_time);
      setValue('not_included_price', formFields.not_included_in_offer || formFields.not_included_price);
      setValue('without_calculation', formFields.free_of_charge || formFields.without_calculation);
      setValue('hours_used', formFields.hours_used ? parseFloat(formFields.hours_used) : null);
      setValue('materials_incl_vat', formFields.materials_incl_vat ? parseFloat(formFields.materials_incl_vat) : null);
      // further_description_for_painting_is_displayed_on_pdf is a text field (separate from create_partner_description category)
      setValue('further_description_for_painting_is_displayed_on_pdf', formFields.further_description_for_painting_is_displayed_on_pdf);
      // create_partner_description is a category field
      formattedData.create_partner_description = getCategoryObject('create_partner_description', formFields.create_partner_description, currentCategories.create_partner_description);
      
      formattedData.task_performed_by = getCategoryObject('task_performed_by', formFields.task_performed_by, currentCategories.task_performed_by);
      
      // Partner field - convert to {item_id: value}
      const partnerValue = formFields.partner;
      if (partnerValue && partnerValue !== '' && partnerValue !== null) {
        // Try to parse as number (item_id)
        const itemId = typeof partnerValue === 'string' ? parseInt(partnerValue, 10) : partnerValue;
        formattedData.partner = isNaN(itemId) ? null : { item_id: itemId };
      } else {
        formattedData.partner = null;
      }
      
      setValue('mk_standard_offer_price', formFields.mk_standard_offer_price ? parseFloat(formFields.mk_standard_offer_price) : null);
      setValue('mk_standard_offer_price_text', formFields.mk_standard_offer_price_text);
      
      formattedData.generate_pdf = getCategoryObject('generate_pdf', formFields.generate_pdf, currentCategories.generate_pdf);
      formattedData.confirmation_email = getCategoryObject('confirmation_email', formFields.confirmation_email, currentCategories.confirmation_email);
      
      setValue('expected_arrival_hour', formFields.expected_arrival_hour);
      setValue('expected_arrival_minute', formFields.expected_arrival_minute);
      
      formattedData.send_sms = getCategoryObject('send_sms', formFields.send_sms, currentCategories.send_sms);
      
      setValue('short_complaint_link', formFields.short_complaint_link);
      
      formattedData.send_complaint_images_to_painter = getCategoryObject('send_complaint_images_to_painter', formFields.send_complaint_images_to_painter, currentCategories.send_complaint_images_to_painter);
      
      setValue('order_comment', formFields.order_comment);
      setValue('material_pickup_date', formatDateToISO(formFields.material_pickup_date));
      setValue('delivery_address', formFields.delivery_address);
      setValue('painter_name_picking_up', formFields.painter_name_picking_up);
      
      formattedData.material_order = getCategoryObject('material_order', formFields.material_order, currentCategories.material_order);
      
      setValue('misc_expenses', formFields.misc_expenses ? parseFloat(formFields.misc_expenses) : null);
      setValue('employee_salary_cost_percent', formFields.employee_salary_cost_percent);
      
      // Date range for date field
      const dateStart = formFields['start-date'] || selectedRow.date_start;
      const dateEnd = formFields['End-date'] || selectedRow.date_end;
      formattedData.date = formatDateRange(dateStart, dateEnd);

      // TODO: Image data - will be added later
      // Image fields that need to be sent:
      // - files: Object with field names as keys and arrays of file objects
      // - files_to_upload: Object with field names and arrays of File objects
      // - files_to_delete: Object with field names and arrays of file IDs
      // Example structure:
      // {
      //   "files": {
      //     "images": [
      //       { "file_id": 123, "filename": "image.jpg", "url": "..." }
      //     ]
      //   }
      // }

    } else if (rowType === 'enquiry' || rowType === 'inquiry') {
      // Inquiry fields mapping - include all fields, set empty ones to null
      setValue('first_name', formFields.first_name);
      setValue('last_name', formFields.last_name);
      setValue('full_address', formFields.full_address);
      setValue('phone_number', formFields.phone_number);
      setValue('email', formFields.email);
      setValue('task_postal_code', formFields.task_postal_code);
      
      formattedData.housing_type = getCategoryObject('housing_type', formFields.housing_type, currentCategories.housing_type);
      
      setValue('task_description', formFields.task_description);
      setValue('task_start_preferred_date', formFields.task_start_preferred_date);
      setValue('agreed_followup', formatDateToISO(formFields.agreed_followup));
      
      formattedData.followup_status = getCategoryObject('followup_status', formFields.followup_status, currentCategories.followup_status);
      
      setValue('subcontractor_conversation_note', formFields.subcontractor_conversation_note);
      setValue('conversation_comment', formFields.conversation_comment);
      
      // Inspection date range
      const inspectionStart = formFields.inspection_date_start || selectedRow.inspection_date_start;
      const inspectionEnd = formFields.inspection_date_end || selectedRow.inspection_date_end;
      formattedData.inspection_date = formatDateRange(inspectionStart, inspectionEnd);
      
      formattedData.status = getCategoryObject('status', formFields.status, currentCategories.status);
      
      formattedData.painterkanon_employee = getCategoryObject('painterkanon_employee', formFields.painterkanon_employee, currentCategories.painterkanon_employee);
      
      formattedData.region = getCategoryObject('region', formFields.region, currentCategories.region);
      
      setValue('task_based_on_invoice', formFields.task_based_on_invoice);
      setValue('task_description_offer_pdf', formFields.task_description_offer_pdf);
      setValue('additional_info', formFields.additional_info);
      setValue('task_price_estimate', formFields.task_price_estimate ? parseFloat(formFields.task_price_estimate) : null);
      setValue('hourly_rate', formFields.hourly_rate ? parseFloat(formFields.hourly_rate) : null);
      setValue('estimated_hours', formFields.estimated_hours ? parseFloat(formFields.estimated_hours) : null);
      setValue('estimate_sent_date', formatDateToISO(formFields.estimate_sent_date));
      setValue('offer_sent_date', formatDateToISO(formFields.offer_sent_date));
      setValue('price_for_the_task', formFields.price_for_the_task ? parseFloat(formFields.price_for_the_task) : null);
      setValue('estimated_days', formFields.estimated_days);
      setValue('free_text_for_additional_services', formFields.free_text_for_additional_services);
      setValue('not_included_in_offer', formFields.not_included_in_offer);
      setValue('free_of_charge', formFields.free_of_charge);
      setValue('task_description_offer2', formFields.task_description_offer2);
      setValue('offer_price_2', formFields.offer_price_2 ? parseFloat(formFields.offer_price_2) : null);
      setValue('offer_sent_date_2', formatDateToISO(formFields.offer_sent_date_2));
      
      formattedData.rooms_to_paint = getCategoryObject('rooms_to_paint', formFields.rooms_to_paint, currentCategories.rooms_to_paint);
      
      // Partner field - convert to {item_id: value}
      const partnerValue = formFields.partner;
      if (partnerValue && partnerValue !== '' && partnerValue !== null) {
        const itemId = typeof partnerValue === 'string' ? parseInt(partnerValue, 10) : partnerValue;
        formattedData.partner = isNaN(itemId) ? null : { item_id: itemId };
      } else {
        formattedData.partner = null;
      }

      // Add product field - send item_id directly
      const addProductValue = formFields.add_product;
      if (addProductValue && addProductValue !== '' && addProductValue !== null) {
        // Parse as number (item_id) and send directly
        const itemId = typeof addProductValue === 'string' ? parseInt(addProductValue, 10) : addProductValue;
        formattedData.add_product = isNaN(itemId) ? null : itemId;
      } else {
        formattedData.add_product = null;
      }

      // TODO: Image data - will be added later
      // Same structure as task images
    }

    return formattedData;
  };

  // Handler for saving form data including file uploads
  const handleSave = async () => {
    setLoading(true);
    
    try {
      const itemId = selectedRow.item_id || selectedRow.id;
      
      // Collect only edited form field values (fields that have changed from original)
      // Keys must match exactly with selectedRow property names (API field names)
      const formFields = {};
      
      // Helper function to normalize values for comparison
      const normalizeValue = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') return val.trim();
        if (typeof val === 'number') return val;
        if (typeof val === 'boolean') return val;
        if (typeof val === 'object' && val.id !== undefined) return String(val.id);
        return String(val);
      };
      
      // Helper function to compare values
      const valuesAreEqual = (val1, val2) => {
        const norm1 = normalizeValue(val1);
        const norm2 = normalizeValue(val2);
        return norm1 === norm2;
      };
      
      if (formRef.current) {
        const formElements = formRef.current.elements;
        const processedElements = new Set();
        
        // Get all form elements and their values
        for (let element of formElements) {
          // Skip buttons, file inputs, and hidden inputs
          if (element.type === 'button' || element.type === 'submit' || element.type === 'file' || element.type === 'hidden') {
            continue;
          }
          
          // Skip view-only fields (readonly or disabled)
          if (element.readOnly || element.disabled) {
            continue;
          }
          
          // Skip if already processed
          if (processedElements.has(element)) {
            continue;
          }
          
          // Get field name - MUST match selectedRow property name exactly (API field name)
          let fieldName = element.name || element.id;
          
          // If no name/id, try to match by value with selectedRow properties
          if (!fieldName) {
            const defaultValue = element.defaultValue;
            const currentValue = element.value || (element.type === 'checkbox' ? element.checked : null);
            
            // Try to find matching property in selectedRow by matching defaultValue
            // This ensures we use the exact API field name from selectedRow
            if (defaultValue !== null && defaultValue !== undefined && defaultValue !== '') {
              const matchingKey = Object.keys(selectedRow).find(key => {
                const rowValue = selectedRow[key];
                // For select fields, compare with ID if it's a category
                if (element.tagName === 'SELECT' && typeof rowValue === 'object' && rowValue !== null && rowValue.id !== undefined) {
                  return String(rowValue.id) === String(defaultValue);
                }
                return valuesAreEqual(rowValue, defaultValue);
              });
              if (matchingKey) {
                fieldName = matchingKey; // Use exact selectedRow key name (API field name)
              }
            }
          }
          
          // Ensure fieldName matches exactly (no extra spaces, correct casing)
          fieldName = fieldName.trim();
          
          // Check if field has explicit name attribute
          const hasExplicitName = element.name && element.name.trim() === fieldName;
          const existsInSelectedRow = selectedRow.hasOwnProperty(fieldName);
          
          // IMPORTANT: If field doesn't have explicit name AND doesn't exist in selectedRow, skip it
          // But if it has explicit name, trust it even if not in selectedRow (for fields like customer_email)
          if (!hasExplicitName && !existsInSelectedRow) {
            // Field name doesn't match any selectedRow property and has no explicit name
            // Log for debugging
            if (element.tagName !== 'BUTTON') {
              console.warn(`⚠️ Field not matched: element type=${element.type || element.tagName}, name="${fieldName}", value="${element.defaultValue || element.value}", no matching selectedRow property and no explicit name`);
            }
            continue;
          }
          
          processedElements.add(element);
          
          // Get current value from form element
          let currentValue = null;
          if (element.type === 'checkbox') {
            currentValue = element.checked;
          } else if (element.type === 'number') {
            currentValue = element.value !== '' ? parseFloat(element.value) : null;
          } else if (element.type === 'date') {
            currentValue = element.value || null;
          } else if (element.tagName === 'SELECT') {
            const selectedOption = element.options[element.selectedIndex];
            if (selectedOption && selectedOption.value !== '') {
              currentValue = selectedOption.value;
            }
          } else if (element.tagName === 'TEXTAREA' || element.tagName === 'INPUT') {
            // For all input types (text, email, tel, phone, etc.), read the value
            currentValue = element.value || null;
          }
          
          // Get original value from selectedRow (use undefined if field doesn't exist in selectedRow)
          const originalValue = existsInSelectedRow ? selectedRow[fieldName] : undefined;
          
          // Collect all form field values (current values from form)
          // We'll format them according to API requirements and set empty values to null
          if (existsInSelectedRow || hasExplicitName) {
            formFields[fieldName] = currentValue;
          }
        }
      }
      
      // Format data according to API requirements
      const formattedData = formatFormDataForAPI(formFields, rowType);
      
      // TODO: Add image data to formattedData when image handling is implemented
      // Image data structure:
      // {
      //   "files": {
      //     "fieldName": [
      //       { "file_id": 123, "filename": "image.jpg", "url": "..." }
      //     ]
      //   },
      //   "files_to_upload": {
      //     "fieldName": [File objects]
      //   },
      //   "files_to_delete": {
      //     "fieldName": [fileIds]
      //   }
      // }
      
      // Log formatted data
      console.log('=== FORM SUBMISSION DATA (FORMATTED FOR API) ===');
      console.log('Row Type:', rowType);
      console.log('Item ID:', itemId);
      console.log('Formatted Data:', JSON.stringify(formattedData, null, 2));
      console.log('Raw Form Fields (before formatting):', formFields);
      
      // Check for empty fields and log them
      const emptyFields = Object.entries(formattedData)
        .filter(([key, value]) => value === null || value === '')
        .map(([key]) => key);
      
      if (emptyFields.length > 0) {
        console.log('Fields set to null (empty values):', emptyFields);
      }
      
      console.log('=== END FORM SUBMISSION DATA ===');

      // Check if there are any changes to save
      const hasFormData = Object.keys(formFields).length > 0;
      const hasFilesToUpload = Object.keys(filesToUpload).length > 0 && 
        Object.values(filesToUpload).some(files => files && files.length > 0);
      const hasFilesToDelete = Object.keys(filesToDelete).length > 0 && 
        Object.values(filesToDelete).some(ids => ids && ids.length > 0);
      
      if (hasFormData || hasFilesToUpload || hasFilesToDelete) {
        // Send formatted data to API
        const endpoint = rowType === 'task' ? '/api/update-task' : '/api/update-inquiry';
        
        console.log('Sending data to API endpoint:', endpoint);
        console.log('Formatted data being sent:', formattedData);
        
        // Check if we need to send files (for both inquiry and task)
        const isInquiry = rowType === 'inquiry' || rowType === 'enquiry';
        const isTask = rowType === 'task';
        const hasImages = (isInquiry || isTask) && (hasFilesToUpload || hasFilesToDelete);
        
        console.log('=== File Upload Check ===');
        console.log('Row Type:', rowType);
        console.log('isInquiry:', isInquiry);
        console.log('isTask:', isTask);
        console.log('hasFilesToUpload:', hasFilesToUpload);
        console.log('hasFilesToDelete:', hasFilesToDelete);
        console.log('hasImages:', hasImages);
        console.log('filesToUpload:', filesToUpload);
        console.log('filesToDelete:', filesToDelete);
        
        try {
          if (hasImages) {
            // Use FormData for file uploads (inquiry or task with images)
            const formData = new FormData();
            
            // Add all form fields
            Object.keys(formattedData).forEach(key => {
              if (key !== 'deletion') {
                const value = formattedData[key];
                if (value !== null && value !== undefined) {
                  if (typeof value === 'object' && !Array.isArray(value)) {
                    formData.append(key, JSON.stringify(value));
                  } else {
                    formData.append(key, value);
                  }
                }
              }
            });
            
            // Add files - for inquiry, use 'file' fieldname; for task, use field name as fieldname
            let totalFiles = 0;
            Object.keys(filesToUpload).forEach(fieldName => {
              const files = filesToUpload[fieldName] || [];
              if (files.length > 0) {
                console.log(`Adding ${files.length} files for field: ${fieldName}`);
                files.forEach(file => {
                  // For inquiry, use 'file' (single field). For task, use field name (multiple fields)
                  const formDataFieldName = isInquiry ? 'file' : fieldName;
                  formData.append(formDataFieldName, file);
                  totalFiles++;
                });
              }
            });
            
            // Add deletion - for inquiry, send as array; for task, send as object with field names
            if (isInquiry) {
              // Inquiry: send as flat array
              let allDeletions = [];
              Object.keys(filesToDelete).forEach(fieldName => {
                const deletions = filesToDelete[fieldName] || [];
                if (deletions.length > 0) {
                  console.log(`Adding ${deletions.length} deletions for field: ${fieldName}`);
                  allDeletions = [...allDeletions, ...deletions];
                }
              });
              
              if (allDeletions.length > 0) {
                console.log('Total deletions to send (inquiry):', allDeletions);
                formData.append('deletion', JSON.stringify(allDeletions));
              }
            } else if (isTask) {
              // Task: send as object with field names as keys
              const deletionObject = {};
              Object.keys(filesToDelete).forEach(fieldName => {
                const deletions = filesToDelete[fieldName] || [];
                if (deletions.length > 0) {
                  console.log(`Adding ${deletions.length} deletions for field: ${fieldName}`);
                  deletionObject[fieldName] = deletions;
                }
              });
              
              if (Object.keys(deletionObject).length > 0) {
                console.log('Deletion object to send (task):', deletionObject);
                formData.append('deletion', JSON.stringify(deletionObject));
              }
            }
            
            console.log('FormData - Total files to upload:', totalFiles);
            console.log('Sending FormData with files');
            
            const response = await postWithFile(endpoint, {
              access_token: access_token,
              data: formData,
            });
            
            console.log('API Response:', response);

            if (response && response.status === 200) {
              showToast("Success", "Data saved successfully!", {
                position: "top-center",
                autoClose: 3000,
              });

              // Reset file states
              setFilesToUpload({});
              setFilesToDelete({});

              // Call onUpdate callback if provided to refresh data
              // Pass the updated data for immediate local state update
              if (onUpdate) {
                onUpdate(response.data);
              }
            } else {
              throw new Error('Unexpected response from API');
            }
          } else {
            // Use JSON for regular updates (no files)
            const response = await postWithToken(endpoint, {
              access_token: access_token,
              data: formattedData,
            });
            
            console.log('API Response:', response);

            if (response && response.status === 200) {
              showToast("Success", "Data saved successfully!", {
                position: "top-center",
                autoClose: 3000,
              });

              // Reset file states
              setFilesToUpload({});
              setFilesToDelete({});

              // Call onUpdate callback if provided to refresh data
              // Pass the updated data for immediate local state update
              if (onUpdate) {
                onUpdate(response.data);
              }
            } else {
              throw new Error('Unexpected response from API');
            }
          }
        } catch (apiError) {
          console.error('API Error:', apiError);
          // Still show the formatted data in console even if API call fails
          console.log('Formatted data (API call failed, but data is ready):', formattedData);
          throw apiError; // Re-throw to be caught by outer catch block
        }
      } else {
        showToast("Info", "No changes to save.", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Error saving form:", error);
      showToast("Error", "Failed to save form. Please try again.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
      <div className="enquiry-overlay" onClick={onOverlayClick}>
      <div className="enquiry-overlay--content">
      <form ref={formRef}>
        <div className="divider-block">
        <button className="enquiry-overlay--close" onClick={onClose}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path d="M18 6L6 18" stroke="#5F6A7C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
<path d="M6 6L18 18" stroke="#5F6A7C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
</svg>
        </button>
        <div className="enquiry-overlay--header">
          <div className="overlaytitle">
            <h2>{selectedRow.title}</h2>
          </div>
          <div className='overlay-description'>
            <div className="description-data">
              <span className="description-data--label">Assigned By:</span>
              <span className="description-data--value">{selectedRow.assignedBy}</span>
            </div>
            <div className="description-data">
              <span className="description-data--label">Created on:</span>
              <span className="description-data--value">{selectedRow.executionDate}</span>
            </div>
            <div className="description-data">
              <span className="description-data--label">Ends on:</span>
              <span className="description-data--value">{selectedRow.deadlineDate}</span>
            </div>
          </div>
        </div>
        <div className="enquiry-overlay-body">
          <div className="enquiry-overlay-form">
            <div className="form-head">
            {rowType === 'task' ? (
               <h3>MK Opgaver App fields </h3>
                 ) : (  
                  <h3>Henvendelse App fields </h3>
                   )}
             
            </div>
            <div className="form-block">
                {/* permission view-only, edit  */}
                {/* class name full col, half col */}
                {rowType === 'task' ? (
                  <TaskFields
                    selectedRow={selectedRow}
                    currentCategories={currentCategories}
                    formatDate={formatDate}
                    stripHtmlAndFormat={stripHtmlAndFormat}
                    getCategoryValue={getCategoryValue}
                    transformCategoryOptions={transformCategoryOptions}
                    handleRemoveImage={handleRemoveImage}
                    handleAddImage={handleAddImage}
                    filesToUpload={filesToUpload}
                    filesToDelete={filesToDelete}
                    API_URL={API_URL}
                    products={products}
                    getProductValue={getProductValue}
                    transformProductOptions={transformProductOptions}
                    partners={partners}
                    getPartnerValue={getPartnerValue}
                    transformPartnerOptions={transformPartnerOptions}
                  />
                ) : (
                  <InquiryFields
                    selectedRow={selectedRow}
                    currentCategories={currentCategories}
                    formatDate={formatDate}
                    stripHtmlAndFormat={stripHtmlAndFormat}
                    getCategoryValue={getCategoryValue}
                    transformCategoryOptions={transformCategoryOptions}
                    handleRemoveImage={handleRemoveImage}
                    handleAddImage={handleAddImage}
                    filesToUpload={filesToUpload}
                    filesToDelete={filesToDelete}
                    API_URL={API_URL}
                    partners={partners}
                    getPartnerValue={getPartnerValue}
                    transformPartnerOptions={transformPartnerOptions}
                    products={products}
                    getProductValue={getProductValue}
                    transformProductOptions={transformProductOptions}
                  />
                )}
                  
            </div>
          </div>
        </div>
        </div>
       
        <div className="enquiry-overlay-footer">
          <div className="enquiry-overlay-footer--link">
            <a href="#" className='link-text' onClick={(e) => { e.preventDefault(); onProductLineClick && onProductLineClick(); }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 8.66667V12.6667C12 13.0203 11.8595 13.3594 11.6095 13.6095C11.3594 13.8595 11.0203 14 10.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V5.33333C2 4.97971 2.14048 4.64057 2.39052 4.39052C2.64057 4.14048 2.97971 4 3.33333 4H7.33333" stroke="#0045DB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 2H14V6" stroke="#0045DB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6.6665 9.33333L13.9998 2" stroke="#0045DB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg> View product line
            </a>
          </div>
          <button 
            className="enquiry-overlay-footer--button" 
            type="button"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
};

export default UserRoleDetailPopup;

